/* global globalThis */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import "./Sidebar.css";
import { FaQuestionCircle } from "react-icons/fa";
import { BarChart3, Zap, Lock } from "lucide-react";
import { DashboardIcon, DataSourceIcon } from "../../assets/icon/svgIcons";
import MyLogo from "../../assets/chatbot.png";

// Internal MenuItem component for modular rendering similar to Code Cheetah
const MenuItem = ({ to, icon: Icon, label, isActive, title, onClick, iconType = "svg", flexDirection, joyrideClass = "" }) => {
  let iconElement;
  if (iconType === "svg") {
    iconElement = <Icon />;
  } else if (iconType === "img") {
    iconElement = <img src={Icon} alt={label} className="menu-icon w-6 h-6" />;
  } else {
    iconElement = <Icon className="menu-icon" />;
  }

  return (
    <li className={isActive ? "active" : ""}>
      <Link
        className={`flex ${flexDirection} sidebar-item-link ${joyrideClass}`}
        to={to}
        title={title}
        onClick={onClick}
      >
        {iconElement}
        <div className="sidebar-label">{label}</div>
      </Link>
    </li>
  );
};

MenuItem.propTypes = {
  to: PropTypes.string.isRequired,
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.string]).isRequired,
  label: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  title: PropTypes.string,
  onClick: PropTypes.func,
  iconType: PropTypes.oneOf(["svg", "img", "react-icon"]),
  flexDirection: PropTypes.string,
  joyrideClass: PropTypes.string,
};

const Sidebar = ({ isOpen }) => {
  const location = useLocation();
  const flexDirection = isOpen ? "flex-row" : "flex-col";
  const isSidebarVisible = !isOpen;

  const getPermissions = () => JSON.parse(sessionStorage.getItem("rolesAndPermission") || "{}");
  const [permissions, setPermissions] = useState(getPermissions());

  useEffect(() => {
    const handlePermUpdate = () => setPermissions(getPermissions());
    globalThis.addEventListener("permissionsUpdated", handlePermUpdate);
    return () => globalThis.removeEventListener("permissionsUpdated", handlePermUpdate);
  }, []);

  const { OSDashboard, OSDataSource, DQ, IssuePredicit, PII, AIAsst } = permissions;

  return (
    <>
      {isSidebarVisible && (
        <div className="sidebar closed">
          <ul className={`sidebar-menu ${isOpen ? "" : "margin-closed"}`}>
            {OSDashboard?.canView && (
              <MenuItem
                to="/dashboard"
                icon={DashboardIcon}
                label="Dashboard"
                isActive={location.pathname === "/dashboard"}
                title="Dashboard"
                flexDirection={flexDirection}
                joyrideClass="sidebar-dashboard-joyride-class"
              />
            )}
            {OSDataSource?.canView && (
              <MenuItem
                to="/data-sources"
                icon={DataSourceIcon}
                label="Data Sources"
                isActive={location.pathname.includes("/data-sources")}
                title="Data Sources"
                flexDirection={flexDirection}
                joyrideClass="sidebar-datasource-joyride-class"
              />
            )}
            {DQ?.canView && (
              <MenuItem
                to="/data-quality"
                icon={BarChart3}
                label="Data Quality"
                isActive={location.pathname === "/data-quality"}
                title="Data Quality"
                iconType="react-icon"
                flexDirection={flexDirection}
                joyrideClass="sidebar-upload-joyride-class"
              />
            )}
            {IssuePredicit?.canView && (
              <MenuItem
                to="/forecasting"
                icon={Zap}
                label="Issue Predict"
                isActive={location.pathname === "/forecasting"}
                title="Issue Predict"
                iconType="react-icon"
                flexDirection={flexDirection}
                joyrideClass="sidebar-forecasting-joyride-class"
              />
            )}
            {PII?.canView && (
              <MenuItem
                to="/pii"
                icon={Lock}
                label="PII"
                isActive={location.pathname === "/pii"}
                title="PII"
                iconType="react-icon"
                flexDirection={flexDirection}
                joyrideClass="sidebar-pii-joyride-class"
              />
            )}
            {AIAsst?.canView && (
              <MenuItem
                to="/ai-assistance"
                icon={MyLogo}
                label="BlueGecko AI"
                isActive={location.pathname.includes("/ai-assistance")}
                title="Bluegecko AI Assistance"
                iconType="img"
                flexDirection={flexDirection}
                joyrideClass="sidebar-ai-joyride-class"
              />
            )}
            <MenuItem
              to="/help"
              icon={FaQuestionCircle}
              label="Help"
              isActive={
                location.pathname === "/view-data" ||
                location.pathname.includes("/history-data") ||
                location.pathname === "/help"
              }
              title="View Data"
              iconType="react-icon"
              flexDirection={flexDirection}
              joyrideClass="sidebar-stt-joyride-class"
            />
          </ul>
        </div>
      )}
    </>
  );
};

Sidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

export default Sidebar;
